import React, { useCallback, useEffect, useState } from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./ChallengeFriendsModal.scss";
import ImageAvatar from "../../../images/Avatar-1.svg";
import ImageCloseIcon from "../../../images/close-icon-purple.svg";
import ImageX from "../../../images/x-outlined-white.svg";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import { useChallengeOnX } from "../../../pages/onboarding/FindFriendsPage/FindFriendsPage";
import { POST_TO_X_LINK } from "../../../utils/constants/links";
import {
  getStoredLastViewedChallengeFriendsModalTime,
  setStoredLastViewedChallengeFriendsModalTime,
} from "../../../utils/storage";
import ChatMessage from "./ChatMessage";
import useAuth from "../../../hooks/useAuth";

const oneYearLater = Date.now() + 1000 * 60 * 60 * 24 * 365;
const currentTime = Date.now();
const MODAL_VIEW_INTERVAL = 3 * 60 * 60 * 1000; // 3 hours

function ChallengeFriendsModal({
  onShowModalComplete,
}: {
  onShowModalComplete?: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { userProfile } = useAuth();

  const onShow = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onHide = useCallback(() => {
    setIsOpen(false);
    onShowModalComplete?.();
  }, [onShowModalComplete]);

  const onCloseBtnClick = useCallback(() => {
    setStoredLastViewedChallengeFriendsModalTime(currentTime);
    onHide();
  }, [onHide]);

  const onCtaClick = useCallback(() => {
    setStoredLastViewedChallengeFriendsModalTime(oneYearLater);
    onHide();
  }, [onHide]);

  const { challengeInvitation, invitationText } = useChallengeOnX();

  useChallengeFriendsModal({
    onShow,
    onHide,
  });

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onCloseBtnClick}
      showCloseBtn={false}
      containerClass="px-0 pt-0"
    >
      <div className="challenge-friends-modal">
        <div className="">
          <p className="py-3 d-flex justify-content-end px-0">
            <GhostButton
              aria-label={t("close")}
              onClick={onCloseBtnClick}
              style={{ marginRight: 4 }}
            >
              <Image src={ImageCloseIcon} alt="close" width={30} />
            </GhostButton>
          </p>
          <ChatMessage
            avatar={userProfile?.avatar?.url || ""}
            message={t("we_are_waiting_for_you")}
            username={userProfile?.username}
          />
          <ChatMessage
            avatar={ImageAvatar}
            message="Share your challenge link. Let's play!"
          />
        </div>
        <div className="py-3 px-3 bg-white text-center">
          <h2 className="mb-2">{t("challenge_friends")}</h2>
          <p className="subhead mb-5">
            {t("your_friends_are_in_our_x_community_key")}!
          </p>
          <PrimaryActionButton
            as="a"
            href={`${POST_TO_X_LINK}?text=${invitationText}`}
            target="_blank"
            rel="noreferrer"
            size="lg"
            leftImage={ImageX}
            text={t("challenge_friends_cta")}
            className="challenge-friends-cta"
            onClick={onCtaClick}
            disabled={!challengeInvitation}
          />
        </div>
      </div>
    </BottomDrawer>
  );
}

export default ChallengeFriendsModal;

function useChallengeFriendsModal({
  onShow,
  onHide,
}: {
  onShow: () => void;
  onHide: () => void;
}) {
  const lastViewedTime = getStoredLastViewedChallengeFriendsModalTime();

  useEffect(() => {
    if (!lastViewedTime) {
      onShow();
      return;
    }
    if (lastViewedTime + MODAL_VIEW_INTERVAL > currentTime) {
      onHide();
      return;
    }
    onShow();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastViewedTime]);
}
